import { ApiError, ApiPromise } from "@/app/api/types";
import { useSession } from "@/app/auth/SessionProvider";
import { useStandardToast } from "@/app/hooks/useStandardToast";

export const apiCall = async <T>(
  input: string | URL | globalThis.Request,
  init?: RequestInit
): ApiPromise<T> => {
  // console.log(input, init);
  const res = await fetch(input, { ...init, credentials: "include" });
  if (res.status === 204) {
    return {} as T;
  }
  try {
    const json = await res.json();
    if (res.status < 400) {
      return json;
    } else {
      console.log(res);
      if (Object.keys(json).length === 0) {
        return {
          is_error: true,
          status: res.status,
          detail: "Something went wrong",
        };
      }
      const value = Object.values(json)[0] as string | string[];
      const detail = typeof value === "string" ? value : value[0];
      return {
        is_error: true,
        status: res.status,
        detail,
      };
    }
  } catch (error) {
    return {
      is_error: true,
      status: res.status,
      detail: "Something went wrong",
    };
  }
};

export const isApiError = <T>(response: T | ApiError): response is ApiError => {
  return !!response && (response as ApiError).is_error;
};

type ApiCallHandler = <T extends any>(
  fn: () => ApiPromise<T>,
  onSuccess: (response: T) => Promise<void> | void,
  onFailure?: () => void
) => Promise<void>;

export const useApiCall = (): ApiCallHandler => {
  const toast = useStandardToast();
  const { setSession } = useSession();

  const createHandler = (): ApiCallHandler => {
    const use: ApiCallHandler = async (fn, onSuccess, onFailure) => {
      const res = await fn();
      if (isApiError(res)) {
        if (res.status === 401) {
          toast({
            title: "Session expired",
            status: "error",
          });
          setSession(false);
          return;
        } else {
          toast({
            title: res.detail,
            status: "error",
          });
          return onFailure && onFailure();
        }
      }
      await onSuccess(res);
      return;
    };

    return use;
  };
  return createHandler();
};
